import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import InactiveIcon from '@mui/icons-material/DoNotDisturb';
import LoopIcon from '@mui/icons-material/Loop';
import WarningIcon from '@mui/icons-material/WarningRounded';
import KitItem from './KitItem';
import { PendingApprovalKit } from '../types';
import CompliancePlip from '../../../../components/CompliancePlip';
import { longMonthYearDateFormat } from '../../../../helpers/date';
import dayjs from 'dayjs';
import { ComplianceStatus } from '../../../../types';
import { useAppSelector } from '../../../../redux/store';
import { selectIsAdminOrOwner } from '../../../../redux/user';
import ApprovalModal from './ApprovalModal';
import styles from '../Shipments.module.css';
import { useParams } from 'react-router-dom';
import { kitImages } from '../../../../helpers/image';
import CompliancePlipGroup from '../../../../components/CompliancePlipGroup';
import useCustomer from '../../../../hooks/useCustomer';

const Kit = ({ kit, refresh }: { kit: PendingApprovalKit; refresh: () => void }) => {
  const theme = useTheme();
  const { kitId } = useParams();

  const complianceStatuses = useMemo(() => {
    const initialStatus = { 1: 0, 2: 0, 3: 0, 4: 0 }

    return kit.medications.reduce((acc: Record<number, number>, item) => {
      acc[item.status]++
      return acc
    }, initialStatus)
  }, [kit.medications])

  const complianceMessages = new Map([
    [ComplianceStatus.Ok, 'Up to Date'],
    [ComplianceStatus.Expiring, 'Attention Needed'],
    [ComplianceStatus.Expired, 'Action Required'],
    [ComplianceStatus.Inactive, 'Inactive'],
  ]);

  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner);

  const customer = useCustomer()

  const [open, setOpen] = useState<boolean>(false);

  const getKitImage = (image: string | undefined) => {
    if (image) {
      const kitImage = kitImages[`${image}.png`];
      return kitImage;
    }
    return kitImages['default-kit-new.png'];
  };

  const isRefillRequested = (kit: PendingApprovalKit) => {
    if (kit.refill_status === 1 && kit.last_refill_date) {
      return dayjs(kit.last_refill_date).diff(dayjs(), 'day') < 3;
    }
    return false;
  };

  const getComplianceIcon = (status: number) => {
    if (status === ComplianceStatus.Ok) {
      return <CheckIcon sx={{ fontSize: '16px', color: theme.palette.success.main }} />;
    } else if (status === ComplianceStatus.Expiring) {
      return <WarningIcon sx={{ fontSize: '16px', color: theme.palette.warning.main }} />;
    } else if (status === ComplianceStatus.Expired) {
      return <WarningIcon sx={{ fontSize: '16px', color: theme.palette.danger.main }} />;
    }

    return <InactiveIcon sx={{ fontSize: '16px', color: theme.palette.inactive.main }} />;
  };

  const handleRefillSuccess = () => {
    setOpen(false);
    refresh();
  };

  useEffect(() => {
    if (!kitId || !customer.customer_number) return;

    if (kitId === String(kit.kit_id)) {
      setOpen(true);
    }
  }, [kitId, customer]);

  const RefillButton = ({ kit }: { kit: PendingApprovalKit }) => (
    <>
      {kit.refill_status === 2 && (
        <Box className={styles.refillStatus}>
          <span className="flex flex-align-center" style={{ color: kit.pending_approval ? theme.palette.danger.main : theme.palette.warning.main }}>
            <LoopIcon fontSize="small" />
            <span className="m-l-8">Refill On Approval</span>
          </span>
          {isAdminOrOwner && kit.pending_approval && (
            <Button
              onClick={() => setOpen(true)}
              sx={{
                ml: 2,
                '&.MuiButton-contained': {
                  backgroundColor: kit.pending_approval ? '#f05050' : '#f4b122',
                  color: theme.palette.common.white
                }
              }}
              variant="contained"
              size="small"
            >
              Review and Approve Refill
            </Button>
          )}
          {isAdminOrOwner && !kit.pending_approval && (
            <Button sx={{ ml: 2 }} variant="contained" size="small" disabled>
              Nothing to Approve
            </Button>
          )}
        </Box>
      )}
    </>
  );

  return (
    <>
      <Card sx={{ mb: 2, color: theme.palette.color50555b.main }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderBottom: 1, borderBottomColor: theme.palette.colordee5e7.main }}>
          {/* Kit Details */}
          <Box sx={{ display: 'flex', flex: 1 }}>
            <img src={getKitImage(kit?.refKit?.images?.trim())} alt="kit" style={{ width: '100px', objectFit: 'contain' }} className="m-r-12" />
            <div>
              <div className="flex flex-align-center">
                <CompliancePlip medium={true} status={kit.status} message={kit?.compliance?.compliance?.message_short} className="m-r-12" />
                <span className="f-s-17 m-r-8">{kit.kit_type}</span>
              </div>
              <Box sx={{ fontSize: '12px', color: theme.palette.inactive.main }}>Kit # {kit.kit_number}</Box>
              <Box sx={{ fontSize: '12px', color: theme.palette.inactive.main }} className="f-s-13">
                Last refilled in {longMonthYearDateFormat(kit.last_refill_date)}
              </Box>
              {isRefillRequested(kit) && (
                <Box sx={{ color: theme.palette.success.main }}>
                  <span>Refill Requested</span>
                </Box>
              )}
            </div>
          </Box>
          {/* Refill Details */}
          <Box sx={{ flex: 2, fontSize: '13px' }}>
            <Box className={styles.refillStatus}>
              {getComplianceIcon(kit.status)} <span className="m-l-8">{kit.compliance.compliance.message_long}</span>
            </Box>
            <RefillButton kit={kit} />
            {isRefillRequested(kit) && (
              <Box className={styles.refillStatus}>
                <span>
                  You have requested a refill for this kit. A customer service representative will contact you shortly to confirm your order.
                </span>
              </Box>
            )}
          </Box>
          {/* Stats */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <CompliancePlipGroup complianceStatuses={complianceStatuses} complianceMessages={complianceMessages} />
          </Box>
        </Box>
        {false && kit.medications.length === 0 ? (
          <Box>No items recorded yet for this kit</Box>
        ) : (
          kit.medications
            .sort((a, b) => {
              const firstValue = new Date(a.status).getTime();
              const secondValue = new Date(b.status).getTime();

              if (firstValue === secondValue) {
                return 0;
              }

              if (firstValue === 4 || firstValue === null) {
                return 1;
              }

              if (secondValue === 4 || secondValue === null) {
                return -1;
              }

              return firstValue > secondValue ? -1 : 1;
            })
            .map((item) => <KitItem key={item.GP_KitItemId} item={item} />)
        )}
      </Card>
      {open && (
        <ApprovalModal
          customer={customer}
          open={open}
          kit={kit}
          onClose={() => setOpen(false)}
          onSuccess={handleRefillSuccess}
        />
      )}
    </>
  );
};

export default Kit;
